import { isString } from 'lodash-es';
import styled from 'styled-components';

import colors from 'utils/colors';
import device from 'utils/device';

import * as Interfaces from './interfaces';

interface ComponentProps {
  long: boolean;
}

/**
 * Main container layout
 */
export const Container = styled.div<ComponentProps>`
  display: flex;
  font-family: VIC, Verdana, Helvetica, sans-serif;
  flex-direction: column;
  align-items: flex-start;
  justify-content: stretch;

  @media ${device.isDesktop}, ${device.isTablet} {
    max-width: ${props => `${props.long ? 664 : 350}px`};
  }
`;

/**
 * Label element
 */
export const Label = styled.label`
  align-self: stretch;
  height: auto;
  font-weight: 600;
  overflow-wrap: break-word;
  color: ${colors.blueGrey};
`;

/**
 * Error label
 */
export const ErrorLabel = styled(Label)`
  color: ${colors.red};
  font-weight: 400;
`;

/**
 * Container for the drop-down <select /> element (used for styling a custom drop-down icon)
 */
export const SelectWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;
  select::-ms-expand {
    display: none;
  }
`;

/**
 * <select /> element
 */
export const Select = styled.select.attrs((props: Interfaces.SelectProps & ComponentProps) => ({
  multiple: props.value instanceof Array,
  required: props.required ?? false,
  long: props.long ?? false
}))<Interfaces.SelectProps>`
  width: 100%;
  height: 48px;
  border-radius: 2px;
  outline: 0px !important;
  border: solid 1px ${props => (props.error && props.touched ? colors.red : colors.blueGrey)};
  background-color: ${colors.white};
  line-height: 1.5em;
  margin: 0;
  font-family: VIC, Verdana, Helvetica, sans-serif;
  color: ${colors.blueGrey};
  padding: 8px 28px 8px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  -webkit-appearance: none; /*Removes default chrome and safari style*/
  -moz-appearance: none; /*Removes default style Firefox*/
  background: transparent;
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMyIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgMTMgOCI+CiAgICA8cGF0aCBmaWxsPSIjRTM3MTBBIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMS4xMzIgMEw2LjUgNC4zMiAxLjg2OCAwIDAgMS44NjIgNi40NiA4IDEzIDEuODYyeiIvPgo8L3N2Zz4=');
  background-repeat: no-repeat;
  /*Position of the background-image*/
  background-position-y: 55%;
  background-position-x: ${props => `${props.long ? 97 : 95}%`};
  font-size: 1rem;
  text-overflow: ellipsis;

  :focus {
    padding: 7px 28px 8px 15px;
    background-position-x: ${props => `${props.long ? 97.2 : 95.3}%`};
  }

  :-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 ${colors.blueGrey};
  }

  //https://codepen.io/maoberlehner/pen/YQBQNj adding placeholder
  :invalid {
    color: ${colors.blueGrey75};
  }

  // Optional select fields fallback.
  &.has-placeholder {
    color: gray;
  }

  [disabled] {
    color: gray;
  }

  :active,
  :focus {
    border: solid 2px ${colors.orange};
  }
`;

/**
 * <option /> element
 */
export const Option = styled.option.attrs((props: Interfaces.Item) => ({
  label: props.label,
  value: isString(props.value) ? props.value : JSON.stringify(props.value)
}))``;

/**
 * unique <option /> serve as a placeholder for the dropdown
 */
export const OptionPlaceholder = styled.option.attrs((props: Interfaces.PlaceholderItem) => ({
  label: props.label,
  value: '',
  hidden: props.hidden,
  disabled: true
}))<Interfaces.PlaceholderItem>``;
